// /opt/ptg01-v1/front-extranet/app/register/page.tsx
// R1 — Inscription en 4 champs (prénom, nom, email, téléphone mobile) + acceptation des CGU.
// Aucun mot de passe : il est choisi depuis le lien de vérification (R3, /auth/verify).
// R2 — écran « Vérifiez votre email » avec renvoi du lien (cooldown 60 s côté UI).
//
// Protections (contrat api-backend 20260920-1708) :
//   • honeypot `company` : champ invisible, doit rester vide ;
//   • nonce signé `GET /api/auth/register-nonce` : la soumission est refusée si elle arrive
//     moins de 3 s après la délivrance du nonce — on attend donc ce délai avant d'envoyer ;
//   • la réponse est TOUJOURS identique (aucune énumération d'adresses) → écran R2 dans tous les cas.
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { UserPlus, Mail, RefreshCw } from 'lucide-react';
import AuthShell, { Banner, Field, INPUT_CLASS, INPUT_ERROR_CLASS, PRIMARY_BUTTON_CLASS } from '@/components/auth/AuthShell';

const PHONE_REGEX = /^(?:(?:\+33|0)[67]\d{8})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NAME_REGEX = /^[\p{L}\p{M}' -]{2,60}$/u;

/** Le nonce est refusé s'il est utilisé en moins de 3 s : on garde une marge. */
const NONCE_MIN_AGE_MS = 3_400;
const RESEND_COOLDOWN_S = 60;

const LANDING_URL = (process.env.NEXT_PUBLIC_LANDING_URL || 'https://www-dev.nexvia-portage.fr').replace(/\/$/, '');
const CGU_URL = `${LANDING_URL}/cgv`;
const PRIVACY_URL = `${LANDING_URL}/politique-rgpd`;

interface FormState { firstName: string; lastName: string; email: string; phone: string; company: string; cgu: boolean }

const EMPTY: FormState = { firstName: '', lastName: '', email: '', phone: '', company: '', cgu: false };

function sleep(ms: number) { return new Promise<void>(r => setTimeout(r, ms)); }

export default function RegisterPage() {
    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
    const [globalError, setGlobalError] = useState('');
    const [loading, setLoading] = useState(false);
    const [sentTo, setSentTo] = useState<string | null>(null);

    // Nonce signé — récupéré au montage, renouvelé si le serveur le refuse.
    const nonce = useRef<{ value: string; at: number } | null>(null);

    const loadNonce = useCallback(async () => {
        try {
            const res = await fetch('/api/auth/register-nonce', { headers: { Accept: 'application/json' } });
            const json = await res.json();
            if (json?.nonce) nonce.current = { value: String(json.nonce), at: Date.now() };
        } catch { /* nonce absent = toléré par l'API (journalisé) */ }
    }, []);

    useEffect(() => { loadNonce(); }, [loadNonce]);

    const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const phoneDigits = form.phone.replace(/\D/g, '');

    function validate(): boolean {
        const next: Partial<Record<keyof FormState, string>> = {};
        if (!NAME_REGEX.test(form.firstName.trim())) next.firstName = 'Prénom : 2 à 60 lettres.';
        if (!NAME_REGEX.test(form.lastName.trim())) next.lastName = 'Nom : 2 à 60 lettres.';
        if (!EMAIL_REGEX.test(form.email.trim())) next.email = 'Adresse email invalide.';
        if (!PHONE_REGEX.test(phoneDigits)) next.phone = 'Numéro mobile attendu : 06 ou 07 suivi de 8 chiffres.';
        if (!form.cgu) next.cgu = 'Vous devez accepter les conditions générales et la politique de confidentialité.';
        setErrors(next);
        return Object.keys(next).length === 0;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setGlobalError('');
        if (!validate()) return;
        setLoading(true);
        try {
            // Le serveur refuse une soumission trop rapide : on patiente plutôt que d'échouer.
            const age = nonce.current ? Date.now() - nonce.current.at : NONCE_MIN_AGE_MS;
            if (age < NONCE_MIN_AGE_MS) await sleep(NONCE_MIN_AGE_MS - age);

            const res = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    email: form.email.trim(),
                    phone: phoneDigits,
                    acceptCgu: true,
                    company: form.company,            // honeypot — doit rester vide
                    nonce: nonce.current?.value,
                }),
            });
            const json = await res.json().catch(() => null);

            if (res.ok && json?.success) {
                setSentTo(form.email.trim());
                return;
            }
            if (json?.code === 'REGISTER_NONCE_INVALID') {
                await loadNonce();
                setGlobalError('Votre formulaire a expiré. Vérifiez vos informations et renvoyez la demande.');
                return;
            }
            if (typeof json?.field === 'string') {
                setErrors(prev => ({ ...prev, [json.field as keyof FormState]: String(json.message || 'Champ invalide.') }));
                return;
            }
            setGlobalError(String(json?.message || "L'inscription n'a pas abouti. Veuillez réessayer."));
        } catch {
            setGlobalError('Erreur de communication avec le serveur. Vérifiez votre connexion et réessayez.');
        } finally {
            setLoading(false);
        }
    };

    if (sentTo) return <CheckYourEmail email={sentTo} />;

    return (
        <AuthShell
            icon={UserPlus}
            title="Rejoignez Nexvia Portage"
            subtitle="Quatre informations suffisent. Vous choisirez votre mot de passe depuis l'email de vérification."
            testId="register-root"
            footer={
                <p className="text-center text-[13px] text-black/60">
                    Déjà inscrit ?{' '}
                    <Link href="/auth/login" className="text-[#000091] hover:text-[#1e40af] font-semibold">Connexion</Link>
                </p>
            }
        >
            {globalError && <Banner tone="error">{globalError}</Banner>}

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {/* Honeypot — hors flux, de taille nulle : invisible pour l'humain (et pour un test),
                    mais présent dans le DOM, donc rempli par les robots qui complètent tous les champs. */}
                <div aria-hidden="true" className="absolute -left-[9999px] top-0 w-0 h-0 overflow-hidden">
                    <label htmlFor="company">Société</label>
                    <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off"
                        data-testid="register-company"
                        className="w-0 h-0 p-0 border-0"
                        value={form.company} onChange={e => update('company', e.target.value)} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Prénom" htmlFor="firstName" error={errors.firstName}>
                        <input id="firstName" type="text" autoComplete="given-name" required
                            data-testid="register-first-name"
                            className={`${INPUT_CLASS} ${errors.firstName ? INPUT_ERROR_CLASS : ''}`}
                            placeholder="Jean"
                            value={form.firstName} onChange={e => update('firstName', e.target.value)} />
                    </Field>
                    <Field label="Nom" htmlFor="lastName" error={errors.lastName}>
                        <input id="lastName" type="text" autoComplete="family-name" required
                            data-testid="register-last-name"
                            className={`${INPUT_CLASS} ${errors.lastName ? INPUT_ERROR_CLASS : ''}`}
                            placeholder="Dupont"
                            value={form.lastName} onChange={e => update('lastName', e.target.value)} />
                    </Field>
                </div>

                <Field label="Adresse email" htmlFor="email" error={errors.email}>
                    <input id="email" type="email" inputMode="email" autoComplete="email" required
                        data-testid="register-email"
                        className={`${INPUT_CLASS} ${errors.email ? INPUT_ERROR_CLASS : ''}`}
                        placeholder="jean.dupont@exemple.com"
                        value={form.email} onChange={e => update('email', e.target.value)} />
                </Field>

                <Field label="Téléphone mobile" htmlFor="phone" error={errors.phone}
                    hint="Nécessaire pour la signature de votre contrat et les échanges avec votre gestionnaire.">
                    <input id="phone" type="tel" inputMode="tel" autoComplete="tel" required
                        data-testid="register-phone"
                        className={`${INPUT_CLASS} ${errors.phone ? INPUT_ERROR_CLASS : ''}`}
                        placeholder="06 12 34 56 78"
                        value={form.phone}
                        onChange={e => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                            update('phone', digits.match(/.{1,2}/g)?.join(' ') || digits);
                        }} />
                </Field>

                <div className="pt-1">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" required
                            data-testid="register-cgu"
                            className="mt-0.5 w-5 h-5 shrink-0 rounded border-slate-300 text-[#000091] focus:ring-2 focus:ring-[#000091] accent-[#000091]"
                            checked={form.cgu} onChange={e => update('cgu', e.target.checked)} />
                        <span className="text-[13px] text-black/75 leading-snug">
                            J&apos;accepte les{' '}
                            <a href={CGU_URL} target="_blank" rel="noopener noreferrer" className="text-[#000091] font-semibold hover:underline">
                                conditions générales
                            </a>{' '}et la{' '}
                            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-[#000091] font-semibold hover:underline">
                                politique de confidentialité
                            </a>.
                        </span>
                    </label>
                    {errors.cgu && <p className="text-[11px] text-rose-700 mt-1.5" role="alert">{errors.cgu}</p>}
                </div>

                <button type="submit" disabled={loading} data-testid="register-submit" className={`${PRIMARY_BUTTON_CLASS} mt-2`}>
                    {loading ? 'Envoi en cours…' : 'Créer mon compte'}
                </button>
            </form>
        </AuthShell>
    );
}

// ─── R2 — « Vérifiez votre email » ───────────────────────────────────────────

function CheckYourEmail({ email }: { email: string }) {
    const [cooldown, setCooldown] = useState(0);
    const [notice, setNotice] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown(c => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const resend = async () => {
        if (cooldown > 0 || sending) return;
        setSending(true);
        try {
            const res = await fetch('/api/auth/resend-verification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const json = await res.json().catch(() => null);
            setNotice(String(json?.message || 'Si cette adresse est associée à un compte non vérifié, un nouveau lien a été envoyé.'));
            setCooldown(RESEND_COOLDOWN_S);
        } catch {
            setNotice("Le renvoi n'a pas abouti. Vérifiez votre connexion et réessayez.");
        } finally {
            setSending(false);
        }
    };

    return (
        <AuthShell
            icon={Mail}
            title="Vérifiez votre email"
            subtitle="Un lien de vérification vient de vous être envoyé."
            testId="register-success"
            footer={
                <p className="text-center text-[13px] text-black/60">
                    <Link href="/auth/login" className="text-[#000091] hover:text-[#1e40af] font-semibold">Retour à la connexion</Link>
                </p>
            }
        >
            <div className="text-center space-y-4">
                <p className="text-[15px] font-bold text-[#000091] break-all" data-testid="register-success-email">{email}</p>
                <p className="text-[13px] text-black/70">
                    Ouvrez cet email et cliquez sur le lien pour choisir votre mot de passe. Le lien reste valable 24 heures.
                </p>
                <p className="text-[12px] text-black/60">
                    Rien reçu ? Regardez dans vos courriers indésirables avant de demander un nouveau lien.
                </p>

                {notice && <Banner tone="info">{notice}</Banner>}

                <button type="button" onClick={resend} disabled={cooldown > 0 || sending}
                    data-testid="register-resend"
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border-2 border-[#000091]/25 bg-white hover:bg-[#000091]/[0.04] text-[#000091] text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <RefreshCw className={`w-4 h-4 ${sending ? 'animate-spin' : ''}`} />
                    {cooldown > 0 ? `Renvoyer dans ${cooldown} s` : 'Renvoyer le lien'}
                </button>
            </div>
        </AuthShell>
    );
}
